import * as React from "react";
import { Link, Toast } from "react-ui";

import { AbsoluteModelPath, isModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { useFormObject } from "../../../FormObjectProvider";
import { currentViewModelSelect } from "../../../../Helpers/StateAccessors/ViewModelSelect";
import { useCandyTheme } from "../../ThemeProvider/CandyTheme/UseCandyTheme";
import { isNotNullOrUndefined } from "../../../../../../Common/TypingUtils";
import { InvalidProgramStateError } from "../../../../../../Common/Errors";
import { extractNameFromComponent } from "../../../TypingUtils";
import { Icon } from "../../../Icons/Icon";

import {
    BindingPath,
    ControlDebugTooltipContentRoot,
    Divider,
    Prop,
    PropName,
    PropsContainer,
    PropValue,
} from "./ControlDebugTooltipContent.styles";
import { ValueViewer } from "./ValueViewer";

interface ControlDebugTooltipContentProps {
    componentName: string;
    bindingPath: AbsoluteModelPath;
    controlProps: Record<string, unknown>;
}

const specialHocPropNames = [
    /^bindingPath$/,
    /^metrics$/,
    /^resolvePath$/,
    /^children$/,
    /^viewModelSelect$/,
    /^processEvaluableProp$/,
    /^on/,
];

export function ControlDebugTooltipContent(props: ControlDebugTooltipContentProps): JSX.Element {
    const form = useFormObject();
    const theme = useCandyTheme();
    const dataDeclarationEntry = form.dataDeclarationHelper.getEntry(props.bindingPath);
    const viewModel = props.bindingPath.isResolved()
        ? currentViewModelSelect.pathViewModel(form.store.getState(), props.bindingPath)
        : "[MultiplveValues]";
    const validationFunction = form.validator.getAggregatedValidationFunctionByPath(props.bindingPath);
    const validationFunctionDebugInfo = validationFunction?.getValidationFunctionDebugInfo();

    function renderProps(): JSX.Element {
        function stringifyObjectProp(value: unknown): string {
            let cache: undefined | Set<unknown> = new Set();
            const result = JSON.stringify(
                value,
                (_, keyValue: unknown): unknown => {
                    if (cache == undefined) {
                        throw new InvalidProgramStateError();
                    }
                    if (typeof keyValue === "object" && keyValue !== null) {
                        if (React.isValidElement(keyValue)) {
                            return `<${extractNameFromComponent(keyValue.type)} />`;
                        }
                        if (cache.has(keyValue)) {
                            return undefined;
                        }
                        cache.add(keyValue);
                    }
                    if (isModelPath(keyValue)) {
                        return keyValue.toString();
                    }
                    return keyValue;
                },
                2
            );
            cache = undefined;
            return result;
        }

        return (
            <>
                {Object.entries(props.controlProps ?? {})
                    .map(([propName, propValue]) => {
                        if (specialHocPropNames.some(x => x.test(propName))) {
                            return undefined;
                        }
                        if (
                            typeof propValue === "number" ||
                            typeof propValue === "boolean" ||
                            typeof propValue === "string"
                        ) {
                            return (
                                <Prop key={propName}>
                                    <PropName title={propName}>{propName}:</PropName>
                                    <PropValue>{JSON.stringify(propValue)}</PropValue>
                                </Prop>
                            );
                        } else if (isModelPath(propValue)) {
                            return (
                                <Prop key={propName}>
                                    <PropName>{propName}:</PropName>
                                    <PropValue>ModelPath({propValue.toString()})</PropValue>
                                </Prop>
                            );
                        } else if (propValue instanceof Object) {
                            if (React.isValidElement(propValue)) {
                                return (
                                    <Prop key={propName}>
                                        <PropName>{propName}:</PropName>
                                        <PropValue>
                                            {"<"}
                                            {extractNameFromComponent(propValue.type)}
                                            {" />"}
                                        </PropValue>
                                    </Prop>
                                );
                            }
                            return (
                                <Prop key={propName}>
                                    <PropName>{propName}:</PropName>
                                    <PropValue>{stringifyObjectProp(propValue)}</PropValue>
                                </Prop>
                            );
                        } else if (propValue == undefined) {
                            return (
                                <Prop key={propName}>
                                    <PropName>{propName}:</PropName>
                                    <PropValue>{propValue == undefined ? "undefined" : "null"}</PropValue>
                                </Prop>
                            );
                        } else {
                            return undefined;
                        }
                    })
                    .filter(isNotNullOrUndefined)}
            </>
        );
    }

    function renderDataDeclaration() {
        return (
            <>
                {Object.entries(dataDeclarationEntry)
                    .map(([propName, propValue]) => (
                        <Prop key={propName}>
                            <PropName title={propName}>{propName}:</PropName>
                            <PropValue>{JSON.stringify(propValue)}</PropValue>
                        </Prop>
                    ))
                    .filter(isNotNullOrUndefined)}
            </>
        );
    }

    function renderViewModel() {
        return (
            <>
                {typeof viewModel === "string"
                    ? viewModel
                    : Object.entries(viewModel)
                          .map(([propName, propValue]) => (
                              <Prop key={propName}>
                                  <PropName title={propName}>{propName}:</PropName>
                                  <PropValue>{JSON.stringify(propValue)}</PropValue>
                              </Prop>
                          ))
                          .filter(isNotNullOrUndefined)}
            </>
        );
    }

    return (
        <ControlDebugTooltipContentRoot>
            {/* @ts-ignore нет иконки и не планируют */}
            <Icon name={"DocsTextIcon"} color={theme.brandColors.primaryColor} /> {props.componentName}
            <br />
            bindingPath:
            <BindingPath
                onClick={async () => {
                    await navigator.clipboard.writeText(props.bindingPath.toString());
                    Toast.push("Путь скопирован!)");
                }}>
                {props.bindingPath.toString()}
            </BindingPath>
            <Divider>Props</Divider>
            <PropsContainer>{renderProps()}</PropsContainer>
            <Divider>Data declaration</Divider>
            {renderDataDeclaration()}
            <Divider>ViewModel</Divider>
            {renderViewModel()}
            <Divider>Validator</Divider>
            {!validationFunctionDebugInfo && "None"}
            {validationFunctionDebugInfo && (
                <>
                    <div>
                        KC validations: {validationFunctionDebugInfo.validationFunctions.length}{" "}
                        <ValueViewer
                            renderValue={() => (
                                <div>
                                    {validationFunctionDebugInfo.validationFunctions.map((x, i) => (
                                        <pre key={i}>{x}</pre>
                                    ))}
                                </div>
                            )}>
                            {open => <Link onClick={open}>show</Link>}
                        </ValueViewer>
                    </div>
                    <div>
                        Validators: {validationFunctionDebugInfo.valueValidators.length}{" "}
                        <ValueViewer
                            renderValue={() => (
                                <div>
                                    {validationFunctionDebugInfo.valueValidators.map((x, i) => (
                                        <pre key={i}>{x}</pre>
                                    ))}
                                </div>
                            )}>
                            {open => <Link onClick={open}>show</Link>}
                        </ValueViewer>
                    </div>
                </>
            )}
        </ControlDebugTooltipContentRoot>
    );
}
